import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { TwoColumnLayout } from '@factorialco/f0-react'
import { QuotePricingPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import QuotePhasesTable from 'modules/finance/components/Quotes/QuotePhasesTable'
import { ConvertToProjectModal } from 'modules/finance/components/Quotes/modals/ConvertToProjectModal'
import { QuotePricingLineItem } from 'modules/finance/components/Quotes/utils'
import { useDownloadQuotePdf } from 'modules/finance/hooks/quotes/useDownloadQuotePdf'
import { onHeaderActionClick } from 'modules/finance/lib/quotesUtils'
import QuotePricingFinancialSummary from 'modules/finance/pages/QuotePricingPage/_QuotePricingFinancialSummary'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useDeleteQuoteDialog } from 'modules/finance/hooks/quotes/useDeleteQuoteDialog'
import { useConvertQuoteIntoInvoice } from 'modules/finance/hooks/quotes/useConvertQuoteIntoInvoice'
import { PreviewQuoteDialog } from 'modules/finance/components/Quotes/dialogs/PreviewQuote'

const QuotePricingPage = () => {
  const { quoteId } = useParams()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [showConvertToProjectModal, setShowConvertToProjectModal] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const {
    access: { locale },
    company: { id: companyId },
  } = useCurrentSession()
  const downloadQuotePdf = useDownloadQuotePdf()
  const { showDeleteQuoteDialog } = useDeleteQuoteDialog()
  const convertQuoteIntoInvoice = useConvertQuoteIntoInvoice()

  const navigateToProject = () => {
    if (quote?.projectId) {
      navigate(
        navigation.projects.id.details.overview.fullPath({
          id: String(quote.projectId),
        })
      )
    }
  }
  const navigateToInvoice = () => {
    if (quoteFinancialDocumentId) {
      navigate(
        navigation.finance.sales.salesInvoices.detail.fullPath({
          invoiceId: Number(quoteFinancialDocumentId),
        })
      )
    }
  }
  const convertToInvoice = () => {
    if (!quoteId || quoteFinancialDocumentId) return
    convertQuoteIntoInvoice(Number(quoteId))
  }
  const { data, error, loading } = useQuery(QuotePricingPageDocument, {
    variables: {
      id: Number(quoteId),
      companyId: Number(companyId),
    },
  })
  const authorized = !!data?.finance?.quote?._policies?.manage?.can
  const quote = data?.finance?.quote
  const totalAmountCents = Number(quote?.totalAmountCents ?? 0)
  const totalProfitCents = Number(quote?.totalProfitCents ?? 0)
  const taxesTotalAmountCents = Number(quote?.taxesTotalAmountCents ?? 0)
  const baseAmountCents = Number(quote?.baseAmountCents ?? 0)
  const baseCostCents = Number(quote?.baseCostCents ?? 0)

  const quoteFinancialDocumentId = quote?.financialDocumentAssociation?.financialDocument?.id

  const castStringToNumber = (value: string | null | undefined): number | null | undefined =>
    value != null ? Number(value) : value

  // Mapping to QuotePricingLineItem with nested financeLineItems; first level = parentId null or type phase
  const lineItems: QuotePricingLineItem[] = useUnsafeMemo(() => {
    const rawItems = quote?.financeLineItems ?? []
    type RawItem = (typeof rawItems)[number]
    // Input type with optional financeLineItems so mapper accepts both top-level and nested items
    type RawLineItemInput = Omit<RawItem, 'financeLineItems'> & {
      financeLineItems?: RawLineItemInput[]
    }
    const mapLineItem = (lineItem: RawLineItemInput): QuotePricingLineItem => {
      const financeLineItems = Array.isArray(lineItem.financeLineItems)
        ? lineItem.financeLineItems.map(mapLineItem)
        : undefined
      return {
        ...lineItem,
        baseCostCents: Number(lineItem.baseCostCents),
        basePriceCents: Number(lineItem.basePriceCents),
        unitCostCents: castStringToNumber(lineItem.unitCostCents),
        unitPriceCents: castStringToNumber(lineItem.unitPriceCents),
        financeLineItems,
      }
    }
    const mapped = rawItems.map((item) => mapLineItem(item))
    return mapped.filter((item) => item.parentId == null || item.type === 'phase')
  }, [quote])

  const isProductQuote = quote?.quoteType == 'products'

  const { has_legacy_job_catalog } = checkFeatures(
    ['legacy_job_catalog'],
    data?.features.featuresConnection.nodes
  )

  const isJobCatalogTreeEnabled = !has_legacy_job_catalog

  return (
    <ResponsivePage.Body
      variant="fullscreen"
      authorized={authorized}
      loading={loading}
      error={error}
      onActionClick={(key) =>
        onHeaderActionClick(
          key,
          Number(quoteId),
          () => setShowConvertToProjectModal(true),
          downloadQuotePdf,
          () =>
            showDeleteQuoteDialog({
              quoteId: Number(quoteId),
              callback: () => navigate(navigation.finance.sales.quotes.fullPath()),
            }),
          () => setShowPreviewModal(true),
          navigateToProject,
          navigateToInvoice,
          convertToInvoice
        )
      }
    >
      {/* TODO: change Layout */}
      <TwoColumnLayout
        sideContent={
          quote &&
          quote.currency && (
            <QuotePricingFinancialSummary
              totalHours={quote.totalHours}
              baseCostCents={baseCostCents}
              baseAmountCents={baseAmountCents}
              taxesTotalAmountCents={taxesTotalAmountCents}
              totalAmountCents={totalAmountCents}
              totalProfitPercentage={quote.totalProfitPercentage}
              totalProfitCents={totalProfitCents}
              currency={quote.currency}
              locale={locale}
              isProductQuote={isProductQuote}
            />
          )
        }
      >
        {quote && quote.currency && (
          <QuotePhasesTable
            currency={quote.currency}
            lineItems={lineItems}
            isProductQuote={isProductQuote}
            isDraftStatus={quote.status === 'draft'}
            isJobCatalogTreeEnabled={isJobCatalogTreeEnabled}
            legalEntityCountry={quote.legalEntity?.country}
            quoteId={quote.id}
          />
        )}
      </TwoColumnLayout>
      {showConvertToProjectModal && quote && quote.currency && quote.name && (
        <ConvertToProjectModal
          isOpen={showConvertToProjectModal}
          onClose={() => setShowConvertToProjectModal(false)}
          currency={quote.currency}
          quoteId={quote.id}
          defaultProjectName={quote.name}
          totalAmountCents={totalAmountCents}
          baseCostCents={baseCostCents}
          totalHours={quote.totalHours}
        />
      )}
      <PreviewQuoteDialog
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        quote={quote}
      />
    </ResponsivePage.Body>
  )
}

export default QuotePricingPage
